import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { DANCE_STYLES } from "../../lib/constants";

const empty = { name: "", phone: "", email: "", age: "", dance_style: "", message: "" };

export default function ContactForm({ dark = false }) {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Please fill in your name, phone and email.");
      return;
    }
    setLoading(true);
    try {
      await api.createEnquiry(form);
      toast.success("Thank you! We'll be in touch very soon.");
      setForm(empty);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const label = dark ? "text-[#F6F4EE]/70" : "text-muted-foreground";
  const field = dark
    ? "bg-transparent border-[rgba(212,175,55,0.3)] text-[#F6F4EE] placeholder:text-[#F6F4EE]/40 focus:border-[var(--rb-gold)]"
    : "bg-card border-border text-foreground placeholder:text-muted-foreground/60 focus:border-[var(--rb-gold)]";
  const base = `w-full border px-4 py-3.5 text-sm outline-none transition-colors duration-300 ${field}`;

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className={`overline text-[10px] mb-2 block ${label}`}>Full Name *</label>
          <input data-testid="contact-name" className={base} value={form.name} onChange={set("name")} placeholder="Your name" />
        </div>
        <div>
          <label className={`overline text-[10px] mb-2 block ${label}`}>Phone *</label>
          <input data-testid="contact-phone" className={base} value={form.phone} onChange={set("phone")} placeholder="+91 ..." />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className={`overline text-[10px] mb-2 block ${label}`}>Email *</label>
          <input data-testid="contact-email" type="email" className={base} value={form.email} onChange={set("email")} placeholder="you@email.com" />
        </div>
        <div>
          <label className={`overline text-[10px] mb-2 block ${label}`}>Age</label>
          <input data-testid="contact-age" className={base} value={form.age} onChange={set("age")} placeholder="e.g. 12" />
        </div>
      </div>
      <div>
        <label className={`overline text-[10px] mb-2 block ${label}`}>Dance Style Interested In</label>
        <select data-testid="contact-dance-style" className={`${base} ${dark ? "[&>option]:text-[#1A0F2E]" : ""}`} value={form.dance_style} onChange={set("dance_style")}>
          <option value="">Select a style</option>
          {DANCE_STYLES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>
      <div>
        <label className={`overline text-[10px] mb-2 block ${label}`}>Message</label>
        <textarea data-testid="contact-message" rows={4} className={base} value={form.message} onChange={set("message")} placeholder="Tell us what you're looking for..." />
      </div>
      <button type="submit" disabled={loading} data-testid="contact-submit" className="btn-gold inline-flex items-center gap-2 px-8 py-4 text-sm font-semibold uppercase tracking-[0.15em] disabled:opacity-70">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {loading ? "Sending..." : "Send Enquiry"}
      </button>
    </form>
  );
}
